import { useMemo } from "react";
import ReactECharts from "echarts-for-react";
import { getChartPalette, chartStateShell } from "./chartTheme";

type BarChartProps = {
  title: string;
  data: {
    categories: string[];
    series: { name: string; data: number[] }[];
  };
  yLabel: string;
  domain?: { min: number; max: number };
  isFetching?: boolean;
  isDarkMode?: boolean;
};

const BarChart = ({
  title,
  data = { categories: [], series: [] },
  yLabel,
  domain,
  isFetching,
  isDarkMode = true,
}: BarChartProps) => {
  const getDisplaySeriesName = (seriesName: string | undefined) => {
    const normalized = String(seriesName ?? "").trim().toLowerCase();
    return normalized === "count" ? title : seriesName || title;
  };

  // ✅ Normalize response structure (memoized so useMemo deps below are stable)
  const normalizedData = useMemo(
    () =>
      data?.categories
        ? data
        : (data as any)?.Student_Footfall ?? { categories: [], series: [] },
    [data]
  );

  // Check if response is successful but categories array is empty
  const isNoDataFound = useMemo(() => {
    const categories = normalizedData.categories || [];
    const series = normalizedData.series || [];
    
    // If categories array length is 0 or less OR series array length is 0 or less, show "No data found"
    return categories.length <= 0 || series.length <= 0;
  }, [normalizedData]);

  const option = useMemo(() => {
    const c = getChartPalette(isDarkMode);
    return {
      tooltip: {
        trigger: "axis",
        confine: true,
        appendToBody: true,
        axisPointer: { type: "shadow" },
        offset: [0, 20],
        formatter: function (params: any[]) {
          const time = params[0]?.axisValue?.replace("\n", " ");
          let tooltipHtml = `<strong>${time}</strong><br/>`;
          params.forEach((item) => {
            tooltipHtml += `${item.marker} ${getDisplaySeriesName(item.seriesName)}: <strong>${item.value}</strong><br/>`;
          });
          return tooltipHtml;
        },
      },
      legend: {
        type: "scroll",
        orient: "horizontal",
        bottom: "10%",
        data: normalizedData.series.map((s:any) => getDisplaySeriesName(s.name)),
        textStyle: {
          color: c.secondaryTextColor,
          fontSize: 12,
        },
      },
      grid: {
        left: "3%",
        right: "4%",
        bottom: "22%",
        top: "15%",
        containLabel: true,
      },
      toolbox: {
        feature: {
          saveAsImage: { title: "Save", name: title },
        },
        iconStyle: {
          borderColor: c.borderColor,
        },
        emphasis: {
          iconStyle: {
            borderColor: c.emphasisIconBorder,
          },
        },
      },
      dataZoom: [
        {
          type: "slider",
          xAxisIndex: 0,
          start: 90,
          end: 100,
          bottom: 10,
          height: 12,
          showDetail: false,
          dataBackground: {
            lineStyle: { color: "transparent" },
            areaStyle: { color: "transparent" },
          },
          handleSize: 8,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true,
          moveOnMouseMove: true,
        },
        {
          type: "inside",
          xAxisIndex: 0,
          zoomOnMouseWheel: false,
          moveOnMouseWheel: true,
          moveOnMouseMove: true,
        },
      ],
      xAxis: {
        type: "category",
        data: normalizedData.categories.map((str:any) => str.replace(/\s*UTC$/, "")),
        axisLabel: {
          rotate: 0,
          formatter: (value: string) => {
            const d = new Date(value);
            return !isNaN(d.getTime())
              ? d.toLocaleDateString()
              : value;
          },
          color: c.secondaryTextColor,
          fontSize: 10,
        },
        axisLine: { lineStyle: { color: c.axisLineColor } },
        axisTick: { lineStyle: { color: c.axisLineColor } },
      },
      yAxis: {
        type: "value",
        name: yLabel,
        min: domain?.min,
        max: domain?.max,
        axisLabel: { color: c.secondaryTextColor, fontSize: 12 },
        axisLine: { lineStyle: { color: c.axisLineColor } },
        splitLine: {
          lineStyle: { color: c.splitLineColor, opacity: 0.5 },
        },
      },
      color: c.colors,
      series: normalizedData.series.map((s: any) => ({
        name: getDisplaySeriesName(s.name),
        type: "bar",
        data: s.data ?? [],
        barMaxWidth: 40,
        barGap: "20%",
      })),
    };
  }, [title, normalizedData, yLabel, domain, isDarkMode]);

  // Show loading message when request is going to backend/response not coming yet
  if (isFetching) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center">
            <div className="h-10 w-10 animate-spin rounded-full border-2 border-slate-300 border-t-blue-500 dark:border-slate-500" />
          </div>
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            Loading chart data...
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Please wait while we fetch the latest insights.
          </p>
        </div>
      </div>
    );
  }

  // Show "no data" message when response comes successfully but categories/series are empty
  if (isNoDataFound) {
    return (
      <div className={chartStateShell}>
        <div className="px-4 text-center">
          <p className="text-sm font-medium text-slate-800 dark:text-slate-200">
            No data found for this chart.
          </p>
          <p className="mt-1 text-xs text-slate-600 dark:text-slate-400">
            Try changing the time range, locations, or filters to see data here.
          </p>
        </div>
      </div>
    );
  }

  return <div style={{ height: '58vh', width: '100%' }}>
  <ReactECharts option={option} style={{ height: '98%', width: '100%' }} />
</div>
};

export default BarChart;
